import { useEffect, useMemo, useRef, useState } from 'react'
import { displayTypeSupportsVisibleItemLimit } from '@feed-widget/surfaces/visibleItemLimit'
import { Button, ErrorSummary, Field, Input, Switch } from '../common'
import styles from './SurfaceContentControls.module.css'

function contentDraft(config, displayType) {
  const limit = config?.visible_item_limit
  return {
    title: typeof config?.title === 'string' ? config.title : '',
    titleHighlight: typeof config?.title_highlight === 'string' ? config.title_highlight : '',
    useSurfaceName: typeof config?.title !== 'string',
    visibleItemLimit: displayTypeSupportsVisibleItemLimit(displayType) && typeof limit === 'number'
      ? String(limit)
      : '',
  }
}

export default function SurfaceContentControls({ surface, onSave, onDirtyChange, contentMode = 'feed' }) {
  const sourceID = surface?.id
  const sourceConfig = surface?.config
  const displayType = surface?.display_type
  const isPrepack = contentMode === 'prepacks'
  const supportsVisibleItemLimit = !isPrepack && displayTypeSupportsVisibleItemLimit(displayType)
  const persisted = useMemo(() => contentDraft(sourceConfig, displayType), [sourceConfig, displayType])
  const [draft, setDraft] = useState(() => contentDraft(sourceConfig, displayType))
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const previousPersisted = useRef({ sourceID, value: persisted })
  const pendingSave = useRef(null)

  useEffect(() => {
    const previous = previousPersisted.current
    previousPersisted.current = { sourceID, value: persisted }
    setDraft((current) => {
      const changedSurface = previous.sourceID !== sourceID
      if (changedSurface) return persisted
      if (pendingSave.current) return current
      const hadUnsavedChanges = JSON.stringify(current) !== JSON.stringify(previous.value)
      const saveLanded = JSON.stringify(current) === JSON.stringify(persisted)
      return !hadUnsavedChanges || saveLanded ? persisted : current
    })
    if (previous.sourceID !== sourceID) setSaveError('')
  }, [persisted, sourceID])

  const dirty = saving || JSON.stringify(draft) !== JSON.stringify(persisted)

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  const update = (field, value) => {
    setSaveError('')
    setDraft((current) => ({ ...current, [field]: value }))
  }

  const save = async () => {
    const config = { ...(sourceConfig || {}) }
    const normalizedTitle = draft.title.trim()
    if (isPrepack) {
      if (draft.useSurfaceName) delete config.title
      else config.title = normalizedTitle
      if (draft.titleHighlight.trim()) config.title_highlight = draft.titleHighlight.trim()
      else delete config.title_highlight
    } else if (normalizedTitle) config.title = normalizedTitle
    else delete config.title

    const normalizedVisibleItemLimit = draft.visibleItemLimit.trim()
    if (supportsVisibleItemLimit) {
      if (normalizedVisibleItemLimit) config.visible_item_limit = Number(normalizedVisibleItemLimit)
      else delete config.visible_item_limit
    }

    const normalizedDraft = {
      titleHighlight: isPrepack ? draft.titleHighlight.trim() : draft.titleHighlight,
      useSurfaceName: typeof config.title !== 'string',
      title: isPrepack && draft.useSurfaceName ? '' : normalizedTitle,
      visibleItemLimit: supportsVisibleItemLimit ? normalizedVisibleItemLimit : draft.visibleItemLimit,
    }

    setDraft(normalizedDraft)
    setSaveError('')
    pendingSave.current = normalizedDraft
    setSaving(true)
    try {
      await onSave({ config })
    } catch (error) {
      const payload = error?.response?.data?.error
      setSaveError(
        (typeof payload === 'string' ? payload : payload?.message)
          || error?.message
          || 'Surface content could not be saved.',
      )
    } finally {
      pendingSave.current = null
      setSaving(false)
    }
  }

  return (
    <div className={styles.controls} data-testid="surface-content-controls">
      {saveError ? <ErrorSummary>{saveError}</ErrorSummary> : null}
      <div className={styles.grid}>
        {isPrepack && (
          <Switch label="Use surface name as heading" checked={draft.useSurfaceName} onChange={(checked) => update('useSurfaceName', checked)} />
        )}
        <Field label="Heading" optional help={isPrepack ? 'Leave blank to hide the heading when not using the surface name.' : 'Shown above this surface in the feed.'}>
          <Input
            value={draft.title}
            disabled={isPrepack && draft.useSurfaceName}
            placeholder={surface?.display_type === 'horizontal_carousel' ? 'Popular parlays' : 'Featured content'}
            maxLength={80}
            onChange={(event) => update('title', event.target.value)}
          />
        </Field>
        {isPrepack && (
          <Field label="Heading highlight" optional help="Text within the heading to highlight.">
            <Input value={draft.titleHighlight} maxLength={80} onChange={(event) => update('titleHighlight', event.target.value)} />
          </Field>
        )}
        {supportsVisibleItemLimit ? (
          <Field
            label="Visible cards"
            optional
            help="Cap how many cards customers see in this carousel. Leave blank to show every eligible card."
          >
            <Input
              type="number"
              min="1"
              max="50"
              step="1"
              value={draft.visibleItemLimit}
              onChange={(event) => update('visibleItemLimit', event.target.value)}
            />
          </Field>
        ) : null}
      </div>
      <div className={styles.actions}>
        <span role="status">{dirty ? 'Unsaved content changes' : 'Surface content is saved'}</span>
        <Button type="button" size="sm" disabled={!dirty || saving} loading={saving} onClick={save}>
          Save content
        </Button>
      </div>
    </div>
  )
}
